import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import LiveTicker from '../shared/LiveTicker';
import { UnifiedAIPanel } from '../ai';
import AnalyzeThisPopover from '../shared/AnalyzeThisPopover';
import useMonarchStore from '@/stores/monarchStore';
import { useChatStore, PANEL_WIDTH_PX } from '@/stores/chatStore';

/* ============================================================================
   AppShell — Layout wrapper with loading skeleton
   ============================================================================ */

interface AppShellProps {
  children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const sidebarCollapsed = useMonarchStore((s) => s.ui.sidebarCollapsed);
  const chatIsOpen = useChatStore((s) => s.isOpen);
  const panelWidth = useChatStore((s) => s.panelWidth);
  const analyzePrompt = useChatStore((s) => s.analyzePrompt);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-screen bg-bg-0">
      <Header />
      <Sidebar />

      {/* LiveTicker — fixed strip below header */}
      <div
        className={`
          fixed top-16 left-0 right-0 z-30
          transition-all duration-300 ease-out
          max-md:ml-0
          ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}
        `}
        style={chatIsOpen ? { marginRight: `min(${PANEL_WIDTH_PX[panelWidth]}px, 40vw)` } : undefined}
      >
        <LiveTicker />
      </div>

      <main
        className={`
          min-h-screen transition-all duration-300 ease-out
          pt-[7.5rem] pb-12 px-6
          max-md:ml-0 max-md:px-4 max-md:pt-[6.5rem] max-md:pb-8
          overflow-x-hidden
          ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}
        `}
        style={chatIsOpen ? { marginRight: `min(${PANEL_WIDTH_PX[panelWidth]}px, 40vw)` } : undefined}
      >
        {children}
      </main>

      {/* AI Chat Panel */}
      <UnifiedAIPanel />

      {/* AnalyzeThis Popover (portal) */}
      {analyzePrompt && (
        <AnalyzeThisPopover
          query={analyzePrompt.query}
          context={analyzePrompt.context}
          anchorRect={analyzePrompt.anchorRect}
          onConfirm={(finalQuery) => useChatStore.getState().confirmAnalyzePrompt(finalQuery)}
          onCancel={() => useChatStore.getState().dismissAnalyzePrompt()}
        />
      )}
    </div>
  );
}

/* ============================================================================
   Loading Skeleton
   ============================================================================ */

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-bg-0">
      <div className="fixed top-0 left-0 right-0 h-16 z-40 bg-[var(--color-header-bg)] border-b border-[var(--color-header-border)] animate-pulse">
        <div className="h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="h-8 w-32 rounded-lg bg-bg-2" />
            <div className="h-8 w-48 rounded-lg bg-bg-1" />
          </div>
          <div className="flex items-center gap-4">
            <div className="h-8 w-24 rounded-lg bg-bg-1" />
            <div className="h-8 w-8 rounded-full bg-bg-2" />
          </div>
        </div>
      </div>

      <div className="fixed top-16 left-0 bottom-0 w-64 z-50 bg-bg-1 border-r border-border">
        <div className="h-14 border-b border-border flex items-center justify-center">
          <div className="h-10 w-40 rounded-lg bg-bg-2 animate-pulse" />
        </div>
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-12 rounded-lg bg-bg-2 animate-pulse"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      </div>

      <div className="ml-64 pt-20 px-8">
        <div className="space-y-6 animate-fade-in">
          <div className="space-y-4">
            <div className="h-10 w-64 rounded-lg bg-bg-2 animate-pulse" />
            <div className="h-6 w-96 rounded-lg bg-bg-1 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-32 rounded-xl border border-border bg-bg-1 p-6 animate-pulse"
                style={{ animationDelay: `${i * 150}ms` }}
              >
                <div className="h-4 w-24 rounded bg-bg-2 mb-4" />
                <div className="h-8 w-32 rounded bg-bg-2" />
              </div>
            ))}
          </div>
          <div className="h-96 rounded-xl border border-border bg-bg-1 p-6 animate-pulse">
            <div className="h-6 w-48 rounded bg-bg-2 mb-6" />
            <div className="h-full rounded bg-bg-2/30" />
          </div>
        </div>
      </div>
    </div>
  );
}
